'use client';

import { useModal } from '@/components/ModalContext';
import Image from 'next/image';
import { useRef, useState } from 'react';
import { A11y, Mousewheel } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import PlayOnPanel from './icons/PlayOnPanel';
import VimeoOverlayPlayer from './VimeoOverlayPlayer';

// Hand-rolled Swiper carousel, following birch's convention (see
// components/product/gallery/ProductCarousel.js) rather than porting
// helix-web's shared <Carousel> wrapper, which doesn't exist here.
//
// Dot indicators are hand-rolled rather than using Swiper's `Pagination`
// module/CSS — no block in birch currently depends on that module (see
// ProductCarousel.js's own custom progress bar for precedent), so this avoids
// introducing an untested dependency path for a single block.
//
// Sizing carries forward two fixes made to helix-web's version the same week
// this was ported (GD-602): the wrapper needs an explicit width (was
// collapsing to content size on tablet/desktop), and the per-breakpoint
// max-width below is tuned so the active (centered, unscaled) slide caps out
// at ~450px tall at every breakpoint instead of just the widest one — solving
// for containerWidth = targetHeight * (9/16) * slidesPerView at each of the
// three slidesPerView breakpoints below (1.2 / 2.2 / 3.2).
export default function VideoCarouselSlider({ data }) {
  const { videos = [] } = data ?? {};
  const { openModal } = useModal();
  const swiperRef = useRef(null);
  const [active, setActive] = useState(Math.floor((videos.length - 1) / 2));

  if (!videos.length) return null;

  const syncSlide = (swiper) => {
    swiperRef.current = swiper;
    setActive(swiper.activeIndex);
  };

  const goTo = (index) => {
    setActive(index);
    swiperRef.current?.slideTo(index);
  };

  const showModal = (video) => {
    openModal(
      <VimeoOverlayPlayer video={video} />,
      'max-w-md',
      'bg-transparent',
      {
        ariaLabel: video?.social_link?.title
          ? `Play video from ${video.social_link.title}`
          : 'Play video',
        noPadding: true,
        fitContent: true,
      },
      'max-h-[90vh]',
    );
  };

  return (
    <div className="mx-auto w-full max-w-76 sm:max-w-139.25 md:max-w-202.5">
      <style>{`
        .video-carousel .swiper-slide {
          transform: scale(0.85);
          transition: transform 0.4s ease;
        }
        .video-carousel .swiper-slide-active {
          transform: scale(1);
        }
        /* Mirrors helix-web's components/global/carousel/Carousel.js, which
           this block deliberately didn't reuse (see file header). Swiper's own
           CSS defaults .swiper to overflow: hidden, which clips neighboring
           slides to this wrapper's own (deliberately narrow, per-breakpoint)
           width — that's the actual cause of slides looking cropped/shrunk on
           mobile and tablet. Helix instead lets the swiper render past its own
           box below 1280px, relying on the *outer* Container's overflow-hidden
           (already present via VideoCarousel.js) — a much wider boundary — to
           do the real clipping. Above 1280px it switches back to clipping at
           the swiper's own edge for a tidy desktop layout. */
        @media (width < 1280px) {
          .video-carousel {
            overflow: visible;
          }
        }
      `}</style>
      <Swiper
        modules={[Mousewheel, A11y]}
        centeredSlides
        initialSlide={active}
        onSwiper={syncSlide}
        onSlideChange={syncSlide}
        className="video-carousel"
        spaceBetween={0}
        slidesPerView={1.2}
        mousewheel={{ forceToAxis: true, releaseOnEdges: true }}
        breakpoints={{
          640: {
            slidesPerView: 2.2,
          },
          768: {
            slidesPerView: 3.2,
          },
        }}
      >
        {videos.map((video, index) => (
          <SwiperSlide key={index}>
            <VideoSlide video={video} showModal={showModal} />
          </SwiperSlide>
        ))}
      </Swiper>

      {videos.length > 1 && (
        <div className="mt-6 flex items-center justify-center gap-2">
          {videos.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => goTo(index)}
              aria-label={`Go to video ${index + 1}`}
              aria-current={index === active}
              className={`h-2 w-2 cursor-pointer rounded-full transition-colors ${
                index === active ? 'bg-primary' : 'bg-sand'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const VideoSlide = ({ video, showModal }) => {
  const thumbnail = video?.video_thumbnail;

  return (
    <button
      type="button"
      className="group relative block w-full cursor-pointer focus:outline-none"
      onClick={() => showModal(video)}
      aria-label="Play video"
    >
      {thumbnail?.url && (
        <Image
          src={thumbnail.url}
          height={thumbnail.dimension.height}
          width={thumbnail.dimension.width}
          className="bg-sand-light aspect-[9/16] w-full rounded-2xl object-cover object-center"
          alt={thumbnail.description || ''}
        />
      )}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <span className="group pointer-events-auto" aria-label="play video">
          <PlayOnPanel />
        </span>
      </div>
    </button>
  );
};
